import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Typography, CircularProgress, Button, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { api } from '../api/client.js';
import { useLang } from '../i18n/lang.js';
import { AUTH_COPY } from '../i18n/authCopy.js';
import { LangToggle } from '../i18n/LangToggle.js';

type State = 'verifying' | 'success' | 'error';

/**
 * Public target of the email-verification link. Reads `?token=` and confirms it
 * against the API. No auth required — the visitor may be signed out.
 */
export function VerifyEmailPage() {
  const [lang] = useLang();
  const c = AUTH_COPY[lang].verify;
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>('verifying');
  const ran = useRef(false); // guard StrictMode double-invoke

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!token) {
      setState('error');
      return;
    }
    api
      .post('/v1/auth/verify-email', { token })
      .then(() => setState('success'))
      .catch(() => setState('error'));
  }, [token]);

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}>
      <Paper sx={{ p: 5, maxWidth: 440, textAlign: 'center' }}>
        <LangToggle sx={{ float: 'right', mt: -2, mr: -2 }} />
        <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
          ReplayOps
        </Typography>

        {state === 'verifying' && (
          <Stack spacing={2} alignItems="center">
            <CircularProgress />
            <Typography color="text.secondary">{c.checking}</Typography>
          </Stack>
        )}

        {state === 'success' && (
          <Stack spacing={2} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
            <Typography variant="h6">{c.okTitle}</Typography>
            <Typography color="text.secondary">{c.okBody}</Typography>
            <Button component={RouterLink} to="/dashboard" variant="contained">
              {c.goToDashboard}
            </Button>
          </Stack>
        )}

        {state === 'error' && (
          <Stack spacing={2} alignItems="center">
            <ErrorOutlineIcon sx={{ fontSize: 56, color: 'warning.main' }} />
            <Typography variant="h6">{c.failedTitle}</Typography>
            <Typography color="text.secondary">{c.failedBody}</Typography>
            <Button component={RouterLink} to="/login" variant="outlined">
              {c.goToSignIn}
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
}
